import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Zap, CheckCircle2, AlertCircle, ShoppingBag, Tag, FileDown, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";

const ELEVATED = ["admin", "dept_lead"];

export default function Settings() {
  const { user } = useAuth();
  const canManageShopify = user && ELEVATED.includes(user.role);
  const [rules, setRules] = useState(null);
  const [shopify, setShopify] = useState(null);
  const [busy, setBusy] = useState("");

  const loadStatus = () => api.get("/shopify/status").then((r) => setShopify(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings/rules").then((r) => setRules(r.data));
    loadStatus();
  }, []);

  const backfill = async () => {
    setBusy("backfill");
    try {
      const { data } = await api.post("/shopify/backfill", null, { params: { days: 90 } });
      toast.success(`Backfill complete: ${data.created} new, ${data.skipped} skipped`);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const registerWebhooks = async () => {
    setBusy("webhooks");
    try {
      const { data } = await api.post("/shopify/webhooks/register");
      const ok = data.results.filter((r) => r.status < 400).length;
      const bad = data.results.length - ok;
      if (bad === 0) toast.success(`All ${ok} webhooks registered`);
      else toast.error(`${ok} ok / ${bad} failed — check scopes`);
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  if (!rules) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageHeader title="Program Rules" subtitle="How commissions, tiers, and rewards are computed" />
      <div className="p-6 md:p-10 space-y-6">
        {/* Shopify integration */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
              <ShoppingBag className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold tracking-tight">Shopify Sync</h3>
              <p className="text-xs text-muted-foreground">Real-time order sync + attribution via discount codes</p>
            </div>
            {shopify?.token_ok && !shopify?.error && (
              <span className="text-xs font-semibold uppercase tracking-widest bg-emerald-500/10 text-emerald-600 rounded-full px-3 py-1 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" /> Connected
              </span>
            )}
            {shopify?.error && (
              <span className="text-xs font-semibold uppercase tracking-widest bg-amber-500/10 text-amber-600 rounded-full px-3 py-1 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" /> Needs Scopes
              </span>
            )}
          </div>

          {shopify ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <Info label="Store" value={shopify.shop || "—"} />
                <Info label="API Version" value={shopify.api_version || "—"} />
                <Info label="Orders Ingested" value={shopify.ingested_orders ?? 0} />
                <Info label="Last Shopify Order" value={shopify.last_order?.order_date?.slice(0, 10) || "—"} />
                <Info label="Last Backfill" value={shopify.last_backfill_at?.slice(0, 16).replace("T", " ") || "Never"} />
                <Info label="Webhooks" value={shopify.webhooks_registered?.length ? shopify.webhooks_registered.join(", ") : "Not registered"} />
              </div>
              {shopify.error && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-700 dark:text-amber-400">
                  <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> {shopify.error}</div>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="shopify-backfill" onClick={backfill} disabled={busy === "backfill" || !shopify.token_ok || !canManageShopify} className="rounded-full">
                  {busy === "backfill" ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                  Backfill Last 90 Days
                </Button>
                <Button data-testid="shopify-webhooks" onClick={registerWebhooks} disabled={busy === "webhooks" || !shopify.token_ok || !canManageShopify} variant="outline" className="rounded-full">
                  <Zap className="h-4 w-4 mr-1.5" /> Register Webhooks
                </Button>
                <Button onClick={loadStatus} variant="ghost" className="rounded-full">Refresh</Button>
                {!canManageShopify && <span className="text-xs text-muted-foreground self-center">Read-only for your role</span>}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Not configured. Set SHOPIFY_STORE_DOMAIN, SHOPIFY_CLIENT_ID, SHOPIFY_CLIENT_SECRET in backend .env.</div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="Customer Discount">
            <div className="text-3xl font-semibold">{rules.customer_discount_percent}%</div>
            <div className="text-sm text-muted-foreground mt-1">Applied on every affiliate code</div>
          </Card>
          <Card title="Payout Cadence">
            <div className="text-lg font-semibold">{rules.payout_cadence}</div>
          </Card>
        </div>

        <Card title="Tier Rules">
          <div className="space-y-2">
            {Object.entries(rules.tier_rules).map(([tier, rule]) => (
              <div key={tier} className="flex gap-3 text-sm"><span className="font-semibold w-32">{tier}</span><span className="text-muted-foreground">{rule}</span></div>
            ))}
          </div>
        </Card>

        <Card title="Commission Slabs">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground uppercase tracking-widest">
                <tr><th className="text-left py-2">Tier</th><th className="text-left py-2">0–3 new</th><th className="text-left py-2">4–10 new</th><th className="text-left py-2">10+ new</th></tr>
              </thead>
              <tbody>
                {Object.entries(rules.commission_slabs).map(([tier, slabs]) => (
                  <tr key={tier} className="border-t border-border/50">
                    <td className="py-3 font-semibold">{tier}</td>
                    {slabs.map((s, i) => <td key={i} className="py-3">{s[2]}%</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Monthly Performance Rewards (Products MRP)">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {rules.performance_reward_slabs.map(([sales, mrp]) => (
              <div key={sales} className="rounded-xl bg-secondary/40 p-3">
                <div className="text-xs text-muted-foreground">₹{sales.toLocaleString()} sales</div>
                <div className="text-lg font-semibold text-primary">₹{mrp.toLocaleString()} MRP</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Fast Track Bonus">
          <div className="text-sm text-muted-foreground">
            Earn <span className="font-semibold text-foreground">₹{rules.fast_track.reward_mrp.toLocaleString()} MRP</span> in free products by acquiring <span className="font-semibold text-foreground">{rules.fast_track.new_customers_required} new customers</span> within <span className="font-semibold text-foreground">{rules.fast_track.window_days} days</span> of the onboarding call. One-time only.
          </div>
        </Card>

        {rules.tier_unlock_rewards && (
          <Card title="Tier Unlock Bonuses (one-time)">
            <div className="text-sm text-muted-foreground space-y-2">
              <div>
                Reach <span className="font-semibold text-foreground">Pro Advanced</span> → unlock <span className="font-semibold text-foreground">₹{rules.tier_unlock_rewards["Pro Advanced"].toLocaleString()} MRP</span> stack.
              </div>
              <div>
                Reach <span className="font-semibold text-foreground">Legend</span> → unlock <span className="font-semibold text-foreground">₹{rules.tier_unlock_rewards["Legend"].toLocaleString()} MRP</span> stack.
              </div>
              <div className="text-xs">Paid out in the same month the tier is first reached.</div>
            </div>
          </Card>
        )}

        {user?.role === "admin" && <ExportNewCustomers />}
        {user?.role === "admin" && <ReconcileWithShopifyCSV />}
        {user?.role === "admin" && <BackfillNewCustomerViaShopify />}
        {canManageShopify && <CustomerBaseImport />}
        {canManageShopify && <DedupeOrdersByRefCard />}
        {canManageShopify && <ShopifyCustomerSync />}
        {canManageShopify && <TagAttributionBackfill />}
        {user?.role === "admin" && <CanonicalizeNewFlags />}
        {user?.role === "admin" && <DedupeNewCustomers />}
        {canManageShopify && <OrdersNeedsReview />}
      </div>
    </div>
  );
}

function ReconcileWithShopifyCSV() {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const run = async (dry) => {
    if (!file) { toast.error("Choose the Shopify Customers CSV first"); return; }
    setBusy(true); setError(""); if (dry) setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/reconcile-new-customers-with-shopify?month=${month}&dry_run=${dry}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const n = data.to_demote_count;
      if (!dry && data.applied) toast.success(`Demoted ${n} orders · ${data.affiliates_affected} affiliates recomputed`);
      else toast(n === 0 ? "Already reconciled — nothing to demote" : `Dry run: would demote ${n} orders`);
    } catch (e) { setError(formatApiError(e)); toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="reconcile-shopify-csv-card" className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-primary" />
        Month-End Reconciliation (vs Shopify Affiliate-Tagged CSV)
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          Definitive month-level fix. Upload the Shopify Customers CSV (filtered to <b>affiliate customer</b> tag).
          Any CRM new-customer flag for the selected month whose email is <b>not</b> in the CSV will be demoted.
        </div>
        <div className="text-xs">
          Locked orders are always skipped. Idempotent. Always dry-run first.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="reconcile-month"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-10 rounded-full w-44"
        />
        <input
          data-testid="reconcile-file"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-secondary file:text-sm hover:file:bg-secondary/80"
        />
        <Button data-testid="reconcile-dryrun" disabled={busy || !file} onClick={() => run(true)} variant="outline" size="sm">
          {busy ? "Running…" : "Dry run"}
        </Button>
        <Button data-testid="reconcile-apply" disabled={busy || !preview || preview.to_demote_count === 0 || !preview.dry_run} onClick={() => run(false)} size="sm">
          Apply demote ({preview?.to_demote_count || 0})
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div className="mt-4 rounded-xl border border-border/60 bg-card p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Info label="Shopify emails" value={preview.shopify_csv_unique_emails} />
            <Info label="Kept (match)" value={preview.kept_matched} />
            <Info label="To demote" value={preview.to_demote_count} />
            <Info label="No email" value={preview.no_email_count} />
            <Info label="Locked (skip)" value={preview.locked_skipped_count} />
          </div>
          {preview.sample_to_demote?.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-rose-500 mb-1.5">
                To demote ({preview.sample_to_demote.length}{preview.to_demote_count > preview.sample_to_demote.length ? ` of ${preview.to_demote_count}` : ""})
              </div>
              <div className="max-h-[260px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground"><th className="px-3 py-2">Order</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Affiliate</th><th className="px-3 py-2 text-right">₹Amount</th></tr></thead>
                  <tbody>{preview.sample_to_demote.map((r) => (<tr key={r.order_id} className="border-t border-border/40"><td className="px-3 py-2 font-mono">{r.order_no}</td><td className="px-3 py-2 text-muted-foreground">{r.order_date}</td><td className="px-3 py-2 truncate max-w-[200px]">{r.email}</td><td className="px-3 py-2 text-muted-foreground truncate max-w-[140px]">{r.affiliate_name}</td><td className="px-3 py-2 text-right">₹{r.amount}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
          )}
          {preview.sample_no_email?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                Phone-attributed rows ({preview.no_email_count}) — inspect manually
              </div>
              <div className="max-h-[160px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground"><th className="px-3 py-2">Order</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Affiliate</th></tr></thead>
                  <tbody>{preview.sample_no_email.map((r) => (<tr key={r.order_id} className="border-t border-border/40"><td className="px-3 py-2 font-mono">{r.order_no}</td><td className="px-3 py-2 text-muted-foreground">{r.order_date}</td><td className="px-3 py-2">{r.phone}</td><td className="px-3 py-2 text-muted-foreground">{r.affiliate}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
          )}
          {!preview.dry_run && preview.applied && <div className="mt-3 text-xs text-emerald-600 font-medium">✓ Applied — affiliates recomputed.</div>}
        </div>
      )}
    </div>
  );
}

function ExportNewCustomers() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/export/new-customers?month=${month}`, { responseType: "blob" });
      const rowCount = res.headers["x-row-count"] || "?";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `new-customers-${month}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rowCount} rows`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="export-new-customers-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <FileDown className="h-4 w-4 text-primary" />
        Export New Customers (CSV)
      </h3>
      <div className="text-sm text-muted-foreground mb-4">
        Downloads every non-cancelled order currently flagged <code>is_new_customer=true</code> for the selected month. Use this to reconcile against Shopify&apos;s customer list.
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="export-nc-month"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-10 rounded-full w-44"
        />
        <Button data-testid="export-nc-download" onClick={download} disabled={busy} size="sm">
          <FileDown className="h-3.5 w-3.5 mr-1.5" />
          {busy ? "Preparing…" : "Download CSV"}
        </Button>
      </div>
      <div className="mt-3 text-xs text-muted-foreground">
        Columns: order_no · order_ref · order_date · customer_email · customer_phone · customer_name · amount · discount_code · affiliate · lock_status · source.
      </div>
    </div>
  );
}

function CanonicalizeNewFlags() {
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const run = async (dry) => {
    setBusy(dry ? "dry" : "apply");
    setError("");
    if (dry) setPreview(null);
    try {
      const { data } = await api.post(`/admin/canonicalize-new-customer-flags?dry=${dry}`);
      setPreview(data);
      const total = data.promoted_to_new + data.demoted_from_new;
      if (!dry) {
        if (total === 0) toast("Nothing to fix — flags already canonical");
        else toast.success(`Fixed ${total} order flags · ${data.affiliates_recomputed} affiliates recomputed`);
      } else {
        toast(total === 0 ? "Flags already canonical" : `${total} orders would change`);
      }
    } catch (e) { setError(formatApiError(e)); toast.error("Failed"); }
    setBusy("");
  };

  const total = preview ? (preview.promoted_to_new + preview.demoted_from_new) : 0;

  return (
    <div data-testid="canonicalize-flags-card" className="rounded-2xl border border-primary/40 bg-primary/5 p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-primary" />
        Restore New-Customer Flags (Canonical)
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          One-shot re-establishes the correct <code>is_new_customer</code> invariant across ALL orders:
          for each customer email, only the <b>earliest non-cancelled order attributed to an affiliate</b> is
          marked new — every other order for that email is not-new.
        </div>
        <div className="text-xs">
          <b>When to use:</b> after a Shopify Customer Sync misfire, or if you suspect flags are drifting.
          Fully idempotent — safe to run any time. Auto-recomputes affected affiliate stats and tiers.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="canonicalize-dryrun" disabled={busy !== ""} onClick={() => run(true)} variant="outline" size="sm">
          {busy === "dry" ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          Dry run
        </Button>
        <Button data-testid="canonicalize-apply" disabled={busy !== "" || !preview || total === 0 || !preview.dry} onClick={() => run(false)} size="sm">
          {busy === "apply" ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          Apply {total > 0 && `(${total})`}
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div className="mt-4 rounded-xl border border-border/60 bg-card p-4 text-sm">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Info label="Promote to NEW" value={preview.promoted_to_new} />
            <Info label="Demote from NEW" value={preview.demoted_from_new} />
            <Info label={preview.dry ? "Affiliates to recompute" : "Affiliates recomputed"} value={preview.dry ? "—" : preview.affiliates_recomputed} />
          </div>
          {preview.sample_promotions?.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-1.5">Promotions (→ NEW)</div>
              <div className="max-h-[180px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground"><th className="px-3 py-2">Email</th><th className="px-3 py-2">Order date</th></tr></thead>
                  <tbody>{preview.sample_promotions.map((p) => (<tr key={p.order_id} className="border-t border-border/40"><td className="px-3 py-2 truncate max-w-[240px]">{p.email}</td><td className="px-3 py-2 text-muted-foreground">{p.order_date}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
          )}
          {preview.sample_demotions?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-rose-500 mb-1.5">Demotions (→ not new)</div>
              <div className="max-h-[180px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground"><th className="px-3 py-2">Email</th><th className="px-3 py-2">Order date</th><th className="px-3 py-2">Reason</th></tr></thead>
                  <tbody>{preview.sample_demotions.map((d) => (<tr key={d.order_id} className="border-t border-border/40"><td className="px-3 py-2 truncate max-w-[220px]">{d.email}</td><td className="px-3 py-2 text-muted-foreground">{d.order_date}</td><td className="px-3 py-2 text-muted-foreground">{d.reason}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
          )}
          {total === 0 && <div className="text-sm text-muted-foreground text-center py-3">All flags are already canonical.</div>}
          {!preview.dry && total > 0 && <div className="mt-3 text-xs text-emerald-600 font-medium">✓ Applied — affiliates recomputed.</div>}
        </div>
      )}
    </div>
  );
}

function DedupeNewCustomers() {
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const run = async (dry) => {
    setBusy(dry ? "dry" : "apply");
    setError("");
    if (dry) setPreview(null);
    try {
      const { data } = await api.post(`/admin/dedupe-new-customers?dry=${dry}`);
      setPreview(data);
      if (!dry) {
        const total = data.duplicate_orders_removed + data.stale_new_flags_corrected;
        if (total === 0) toast("Nothing to clean — already tidy");
        else toast.success(`Cleaned ${total} rows across ${data.affiliates_recomputed} affiliates`);
      } else {
        const total = data.duplicate_orders_removed + data.stale_new_flags_corrected;
        toast(total === 0 ? "No issues found" : `${total} rows would be cleaned`);
      }
    } catch (e) { setError(formatApiError(e)); toast.error("Failed"); }
    setBusy("");
  };

  const totalToFix = preview ? (preview.duplicate_orders_removed + preview.stale_new_flags_corrected) : 0;

  return (
    <div data-testid="dedupe-new-customers-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-primary" />
        New-Customer Data Hygiene
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          One-shot cleanup that fixes two known count-inflation bugs: (a) same Shopify order ingested more than once (keeps the earliest, deletes duplicates), and (b) an order flagged <code>is_new_customer=true</code> when the same email already had an earlier non-cancelled order (flips to false). Auto-recomputes affected affiliates.
        </div>
        <div className="text-xs">Safe to run repeatedly — idempotent. Always dry-run first.</div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="dedupe-dryrun" disabled={busy !== ""} onClick={() => run(true)} variant="outline" size="sm">
          {busy === "dry" ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          Dry run
        </Button>
        <Button data-testid="dedupe-apply" disabled={busy !== "" || !preview || totalToFix === 0 || !preview.dry} onClick={() => run(false)} size="sm">
          {busy === "apply" ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          Apply cleanup {totalToFix > 0 && `(${totalToFix})`}
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div data-testid="dedupe-preview" className="mt-4 rounded-xl border border-border/60 bg-secondary/20 p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <Info label="Duplicate orders" value={preview.duplicate_orders_removed} />
            <Info label="Stale new flags" value={preview.stale_new_flags_corrected} />
            <Info label={preview.dry ? "Affiliates to recompute" : "Affiliates recomputed"} value={preview.dry ? "—" : preview.affiliates_recomputed} />
          </div>

          {preview.duplicate_details?.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Duplicate order refs</div>
              <div className="max-h-[200px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-3 py-2">Shopify order_ref</th>
                      <th className="px-3 py-2 text-right">Copies</th>
                      <th className="px-3 py-2 text-right">Removing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.duplicate_details.map((d) => (
                      <tr key={d.order_ref} className="border-t border-border/40">
                        <td className="px-3 py-2 font-mono">{d.order_ref}</td>
                        <td className="px-3 py-2 text-right">{d.copies}</td>
                        <td className="px-3 py-2 text-right font-semibold text-rose-500">{d.deleting}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.stale_details?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Stale &quot;new&quot; flags to flip ({preview.stale_details.length}{preview.stale_new_flags_corrected > preview.stale_details.length ? ` of ${preview.stale_new_flags_corrected}` : ""})
              </div>
              <div className="max-h-[260px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Order flagged &quot;new&quot;</th>
                      <th className="px-3 py-2">Earlier order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.stale_details.map((s, i) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="px-3 py-2 truncate max-w-[220px]">{s.email}</td>
                        <td className="px-3 py-2 text-muted-foreground">{s.flip_order_date?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-2 text-muted-foreground">{s.earlier_order_date?.slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totalToFix === 0 && (
            <div className="text-sm text-muted-foreground text-center py-3">No issues found. Data is clean.</div>
          )}
          {!preview.dry && totalToFix > 0 && (
            <div className="mt-3 text-xs text-emerald-600 font-medium">✓ Applied — affiliate lifetime counts, tiers, and Fast-Track statuses recomputed.</div>
          )}
        </div>
      )}
    </div>
  );
}

function BackfillNewCustomerViaShopify() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1); // default: last month (typical use case)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const run = async (dry) => {
    setBusy(dry ? "dry" : "apply");
    setError("");
    if (dry) setPreview(null);
    try {
      const { data } = await api.post(
        `/admin/backfill-new-customer-flags-via-shopify?month=${month}&source=shopify&dry_run=${dry}`,
      );
      setPreview({ ...data, dry });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="backfill-shopify-orders-count" className="rounded-3xl border border-border/60 bg-card p-6">
      <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-primary" /> Live-Shopify New-Customer Backfill
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          For each <code>is_new_customer=true</code> Shopify-source order in the selected month, calls Shopify Admin API to fetch the customer&apos;s true lifetime <code>orders_count</code>. If &gt; 1, demotes to <code>false</code> and recomputes the affiliate. Skips locked orders and Bixgrow-source rows.
        </div>
        <div className="text-xs">Use for one-off cleanups (e.g. July had 13 false-positives). Always dry-run first.</div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="backfill-month-input"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
        />
        <Button data-testid="backfill-dryrun" disabled={busy !== ""} onClick={() => run(true)} variant="outline" size="sm">
          {busy === "dry" ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          Dry run
        </Button>
        <Button
          data-testid="backfill-apply"
          disabled={busy !== "" || !preview || preview.demoted_count === 0 || !preview.dry}
          onClick={() => run(false)}
          size="sm"
        >
          {busy === "apply" ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          Apply {preview?.demoted_count > 0 && `(demote ${preview.demoted_count})`}
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div className="mt-4 rounded-2xl bg-secondary/40 p-4 text-sm space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Info label="Scanned" value={preview.scanned} />
            <Info label="Would demote" value={preview.demoted_count} />
            <Info label="Kept as new" value={preview.kept_new_count} />
            <Info label="Unresolved" value={preview.unresolved_count} />
          </div>
          <div className="text-[11px] text-muted-foreground pt-1">
            {preview.applied
              ? `✓ Applied — ${preview.affiliates_affected} affiliates recomputed.`
              : preview.dry
                ? `Dry run — no changes yet. Click Apply to commit.`
                : ""}
          </div>
          {preview.sample_demoted?.length > 0 && (
            <details className="pt-1">
              <summary className="cursor-pointer text-xs text-primary hover:underline">
                Show {Math.min(preview.sample_demoted.length, 100)} customers to demote
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-xl bg-background border border-border/50">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/40 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Customer</th>
                      <th className="text-left px-3 py-2 font-semibold">Affiliate</th>
                      <th className="text-right px-3 py-2 font-semibold">Shopify orders_count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_demoted.map((d) => (
                      <tr key={d.order_id} className="border-t border-border/40">
                        <td className="px-3 py-1.5">
                          <div className="font-medium">{d.customer_name || d.customer_email}</div>
                          <div className="text-[10px] text-muted-foreground">{d.customer_email}</div>
                        </td>
                        <td className="px-3 py-1.5">{d.affiliate_name || "—"}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{d.shopify_orders_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}



function ShopifyCustomerSync() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [forceMode, setForceMode] = useState(false);

  const run = async (dry) => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setBusy(true); setError(""); setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/shopify-customers-sync?dry_run=${dry}&force=${forceMode}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const flipped = data.orders_to_flip || 0;
      const review = data.needs_review_count || 0;
      if (!dry && data.applied) toast.success(`Fixed ${flipped} orders across ${data.affiliates_affected} affiliates`);
      else if (forceMode) toast(`Dry run: ${flipped} orders would flip`);
      else toast(`Dry run: ${review} orders need review (safe mode — no auto-flip)`);
    } catch (e) { setError(formatApiError(e)); toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="shopify-customer-sync-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <ShoppingBag className="h-4 w-4 text-primary" />
        Shopify Customer Sync
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          Detects customers our CRM flagged &quot;new&quot; who actually already existed on Shopify. <b>Force</b> mode trusts Shopify&apos;s Total Orders count and flips them; <b>Safe</b> mode surfaces them for you to review manually.
        </div>
        <div className="text-xs">
          <b>How to get the CSV:</b> Shopify admin → <b>Customers</b> → <b>Export</b> → <em>Export all customers</em> → CSV. Only <code>Email</code> and <code>Total Orders</code> columns are used.
        </div>
        <div className="text-xs">
          Orders you have <b>locked</b> via the per-order lock button will always be skipped by this tool.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="shopify-csv-file"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-secondary file:text-sm hover:file:bg-secondary/80"
        />
        <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
          <input
            data-testid="shopify-force-toggle"
            type="checkbox"
            checked={forceMode}
            onChange={(e) => setForceMode(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Force flip (trust Shopify totals)
        </label>
        <Button data-testid="shopify-csv-dryrun" disabled={busy || !file} onClick={() => run(true)} variant="outline" size="sm">
          {busy ? "Running…" : "Dry run"}
        </Button>
        <Button data-testid="shopify-csv-apply" disabled={busy || !file || !preview || !forceMode || preview.orders_to_flip === 0} onClick={() => run(false)} size="sm">
          Apply fixes
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div data-testid="shopify-csv-preview" className="mt-4 rounded-xl border border-border/60 bg-secondary/20 p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Info label="CSV rows" value={preview.csv_rows_parsed} />
            <Info label={forceMode ? "Would flip" : "Needs review"} value={forceMode ? preview.orders_to_flip : preview.needs_review_count} />
            <Info label="Already correct" value={preview.already_correct} />
            <Info label="Locked (skipped)" value={preview.locked_skipped_count || 0} />
          </div>
          {(forceMode ? preview.preview : preview.needs_review)?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {forceMode ? "Sample of flips" : "Sample needing review"} ({(forceMode ? preview.preview : preview.needs_review).length}{" of "}{forceMode ? preview.orders_to_flip : preview.needs_review_count})
              </div>
              <div className="max-h-[260px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Our earliest order</th>
                      <th className="px-3 py-2 text-right">Our count</th>
                      <th className="px-3 py-2 text-right">Shopify total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((p) => (
                      <tr key={p.order_id} className="border-t border-border/40">
                        <td className="px-3 py-2">{p.email}</td>
                        <td className="px-3 py-2 text-muted-foreground">{p.our_earliest_order_date}</td>
                        <td className="px-3 py-2 text-right">{p.our_count}</td>
                        <td className="px-3 py-2 text-right font-semibold">{p.shopify_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {preview.applied && <div className="mt-3 text-xs text-emerald-600 font-medium">✓ Applied — affiliate lifetime counts and tiers recomputed.</div>}
        </div>
      )}
    </div>
  );
}

function CustomerBaseImport() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [applied, setApplied] = useState(false);

  const run = async (dry) => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setBusy(true); setError(""); setPreview(null); setApplied(false);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/import-customer-base?dry_run=${dry}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setPreview(data);
      if (!dry) {
        setApplied(true);
        toast.success(`Imported ${data.inserted + data.updated} customers (${data.inserted} new, ${data.updated} updated)`);
      } else {
        toast(`Dry run: ${data.would_insert} new + ${data.would_update} to update`);
      }
    } catch (e) { setError(formatApiError(e)); toast.error("Import failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="customer-base-import-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <ShoppingBag className="h-4 w-4 text-primary" />
        Customer Base Import (Never-New Guard)
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          Uploads Shopify&apos;s full customer export CSV. Any email or phone in this file is treated as a <b>pre-existing customer</b> — so the first time they use an affiliate code, they will <b>not</b> be stamped &quot;new&quot;. Applies to all future webhook ingestions.
        </div>
        <div className="text-xs">
          <b>How to get the CSV:</b> Shopify admin → <b>Customers</b> → <b>Export</b> → <em>Export all customers</em> → CSV. We use <code>Email</code>, <code>Phone</code>, <code>Default Address Phone</code>, and <code>Total Orders</code>.
        </div>
        <div className="text-xs">
          <b>Idempotent</b> — re-uploading refreshes existing rows. No historical orders are touched (going-forward only).
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="customer-base-file"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-secondary file:text-sm hover:file:bg-secondary/80"
        />
        <Button data-testid="customer-base-dryrun" disabled={busy || !file} onClick={() => run(true)} variant="outline" size="sm">
          {busy ? "Running…" : "Dry run"}
        </Button>
        <Button data-testid="customer-base-apply" disabled={busy || !file} onClick={() => run(false)} size="sm">
          Import
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div data-testid="customer-base-preview" className="mt-4 rounded-xl border border-border/60 bg-secondary/20 p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Info label="CSV rows" value={preview.csv_rows} />
            <Info label="Skipped (no email)" value={preview.skipped_no_email} />
            <Info label={applied ? "Inserted" : "Would insert"} value={applied ? preview.inserted : preview.would_insert} />
            <Info label={applied ? "Updated" : "Would update"} value={applied ? preview.updated : preview.would_update} />
          </div>
          {applied && (
            <div className="mt-3 text-xs text-emerald-600 font-medium">
              ✓ Imported. Total known customers now tracked: <b>{preview.total_known_customers}</b>. Going forward, these customers will never be stamped &quot;new&quot; by an affiliate code.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DedupeOrdersByRefCard() {
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (dry) => {
    setBusy(true); setError(""); if (dry) { setPreview(null); setApplied(null); }
    try {
      const { data } = await api.post(`/admin/dedupe-orders-by-ref?dry_run=${dry}`);
      if (dry) setPreview(data);
      else { setApplied(data); toast.success(`Removed ${data.orders_removed} duplicate orders`); }
    } catch (e) { setError(formatApiError(e)); toast.error("Cleanup failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="dedupe-orders-by-ref-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <Copy className="h-4 w-4 text-primary" />
        Dedupe Orders by Order Ref
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          Removes duplicate order rows that share the same <code>order_ref</code> — the exact signature of the Shopify webhook race (order fired twice by Flow + native, retries, or orders/create + orders/updated in parallel).
        </div>
        <div className="text-xs">
          <b>Keeps</b>: earliest Shopify-sourced row per group. <b>Removes</b>: the rest. Also reverses affected affiliates&apos; <code>lifetime_new_customers</code> counters and re-runs tier/commission recompute.
        </div>
        <div className="text-xs">
          After cleanup, the endpoint also (re-)creates the unique partial index <code>uniq_shopify_order_ref</code> so future duplicates are blocked at the DB layer. If the index couldn&apos;t be created earlier due to pre-existing duplicates, this fixes that too.
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <Button data-testid="dedupe-ref-dryrun" onClick={() => run(true)} disabled={busy} variant="outline" size="sm">{busy ? "Running…" : "Dry run"}</Button>
        <Button data-testid="dedupe-ref-apply" onClick={() => run(false)} disabled={busy || !preview || preview.orders_to_remove === 0} size="sm">Apply cleanup</Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && !applied && (
        <div data-testid="dedupe-ref-preview" className="mt-4 rounded-xl border border-border/60 bg-secondary/20 p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Info label="Duplicate groups" value={preview.duplicate_groups} />
            <Info label="Orders to remove" value={preview.orders_to_remove} />
            <Info label="Affiliates affected" value={preview.affiliates_affected} />
          </div>
          {preview.preview?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Sample groups</div>
              <div className="space-y-2 max-h-72 overflow-auto">
                {preview.preview.map((g, i) => (
                  <div key={i} className="text-xs font-mono border border-border/40 rounded p-2 bg-background">
                    <div><b>Ref:</b> {g.order_ref} · <b>Copies:</b> {g.count}</div>
                    <div className="text-emerald-600">Keep: {g.kept.source || "?"} · {g.kept.date?.slice(0,10)}</div>
                    <div className="text-destructive">Remove: {g.removing.map((r) => `${r.source || "?"}@${r.date?.slice(0,10)}`).join(" · ")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {applied && (
        <div data-testid="dedupe-ref-applied" className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Info label="Groups found" value={applied.duplicate_groups_found} />
            <Info label="Orders removed" value={applied.orders_removed} />
            <Info label="Affiliates affected" value={applied.affiliates_affected} />
          </div>
          <div className="mt-3 text-xs">
            <b>Unique index:</b> <span className={applied.unique_index_status?.startsWith("failed") ? "text-destructive" : "text-emerald-600"}>{applied.unique_index_status}</span>
            {applied.unique_index_status?.startsWith("failed") && <div className="mt-1 text-muted-foreground">Some duplicates may still be present — dry-run again to see what&apos;s left.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function TagAttributionBackfill() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const run = async (dry) => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setBusy(true); setError(""); setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/reattribute-by-tags?dry_run=${dry}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (!dry && data.applied) toast.success(`Attributed ${data.attributions_to_apply} orders across ${data.affiliates_touched} affiliates`);
      else toast(`Dry run: ${data.attributions_to_apply} orders would be attributed`);
    } catch (e) { setError(formatApiError(e)); toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="tag-attribution-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <Tag className="h-4 w-4 text-primary" />
        Tag-based Attribution Backfill
      </h3>
      <div className="text-sm text-muted-foreground mb-4 space-y-1.5">
        <div>
          Attributes historical Shopify orders that came in via referral link (no discount code) but carry the affiliate&apos;s name as a Shopify order tag.
        </div>
        <div className="text-xs">
          <b>How to get the CSV:</b> Shopify admin → <b>Orders</b> → <b>Export</b> → <em>All orders</em> → CSV. Only <code>Name</code> (order name) and <code>Tags</code> columns are used. Live webhook orders going forward are auto-attributed.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="tag-attr-file"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-secondary file:text-sm hover:file:bg-secondary/80"
        />
        <Button data-testid="tag-attr-dryrun" disabled={busy || !file} onClick={() => run(true)} variant="outline" size="sm">
          {busy ? "Running…" : "Dry run"}
        </Button>
        <Button data-testid="tag-attr-apply" disabled={busy || !file || !preview || preview.attributions_to_apply === 0} onClick={() => run(false)} size="sm">
          Apply attributions
        </Button>
      </div>
      {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      {preview && (
        <div data-testid="tag-attr-preview" className="mt-4 rounded-xl border border-border/60 bg-secondary/20 p-4 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Info label="Rows parsed" value={preview.csv_orders_parsed} />
            <Info label="Would attribute" value={preview.attributions_to_apply} />
            <Info label="Already attributed" value={preview.already_attributed} />
            <Info label="No tag match" value={preview.no_tag_match} />
            <Info label="Not in DB" value={preview.not_in_db} />
          </div>
          {preview.preview?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Sample ({preview.preview.length}{preview.attributions_to_apply > preview.preview.length ? ` of ${preview.attributions_to_apply}` : ""})
              </div>
              <div className="max-h-[300px] overflow-y-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-3 py-2">Order</th>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">Matched tag</th>
                      <th className="px-3 py-2">Affiliate</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((p) => (
                      <tr key={p.order_id} className="border-t border-border/40">
                        <td className="px-3 py-2 font-mono">{p.shopify_number || p.order_id.slice(0, 8)}</td>
                        <td className="px-3 py-2 truncate max-w-[180px]">{p.customer_email}</td>
                        <td className="px-3 py-2">
                          <span className="inline-block px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold text-[10px]">
                            {p.matched_tag}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium">{p.affiliate_name}</td>
                        <td className="px-3 py-2 text-right">₹{(p.amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {preview.applied && <div className="mt-3 text-xs text-emerald-600 font-medium">✓ Applied — commissions computed and affiliate stats recomputed.</div>}
        </div>
      )}
    </div>
  );
}

function OrdersNeedsReview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/orders-needs-review").then((r) => setData(r.data)).catch(() => setData({ groups: [] }));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const groups = data.groups || [];
  const resolve = async (keep_order_id, group) => {
    setBusy(keep_order_id);
    try {
      const remove_order_ids = group.order_ids.filter((id) => id !== keep_order_id);
      await api.post("/admin/orders-needs-review/resolve", { keep_order_id, remove_order_ids });
      toast.success(`Kept 1 order, removed ${remove_order_ids.length}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const dismissGroup = async (group) => {
    setBusy(`dismiss-${group.order_ids[0]}`);
    try {
      await api.post("/admin/orders-needs-review/dismiss", { order_ids: group.order_ids });
      toast.success("Marked as all legitimate — group dismissed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  return (
    <div data-testid="orders-needs-review-card" className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-2 flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-amber-500" />
        Orders Needing Review
        <span className="text-xs text-muted-foreground font-normal">
          · {groups.length} group{groups.length !== 1 ? "s" : ""} · same email/day/amount, 3+ rows
        </span>
      </h3>
      {groups.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Nothing to review — all clean.</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g, gi) => (
            <div key={gi} data-testid={`review-group-${gi}`} className="rounded-xl border border-border/60 p-4">
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <span className="font-semibold">{g.email}</span>
                <span className="text-xs text-muted-foreground">{g.day}</span>
                <span className="text-xs">₹{g.amount.toLocaleString()}</span>
                <span className="text-[10px] uppercase tracking-widest bg-amber-500/10 text-amber-700 rounded-full px-2 py-0.5">
                  {g.count} rows · {g.sources.join(" + ")}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mb-2">
                Pick the row to keep — the others will be removed. If they&apos;re all real (bulk purchase), dismiss.
              </div>
              <div className="grid gap-2">
                {g.order_ids.map((oid, i) => (
                  <div key={oid} className="flex items-center justify-between bg-secondary/30 rounded-lg px-3 py-2">
                    <div className="text-xs">
                      <span className="font-mono">{g.order_refs[i] || oid.slice(0, 8)}</span>
                    </div>
                    <button
                      data-testid={`review-keep-${gi}-${i}`}
                      disabled={busy === oid}
                      onClick={() => resolve(oid, g)}
                      className="text-xs px-3 py-1 rounded-full bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                      Keep this
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  data-testid={`review-dismiss-${gi}`}
                  disabled={busy.startsWith("dismiss")}
                  onClick={() => dismissGroup(g)}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  All {g.count} are legitimate — dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6">
      <h3 className="text-lg font-semibold tracking-tight mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl bg-secondary/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-1 truncate">{value}</div>
    </div>
  );
}
